import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

const SITE = 'https://githubmate.ai';
const PAGE_URL = `${SITE}/ai-coding-tools`;

type Tier = 'S' | 'A' | 'B';

interface AiTool {
  name: string;
  company: string;
  url: string | null;
  tagline: string;
  description: string;
  pricing: string;
  tier: Tier;
  status?: 'legacy' | 'private-beta' | 'deprecated';
}

const TIER_INFO: Record<Tier, { label: string; subtitle: string; description: string }> = {
  S: {
    label: 'Tier S',
    subtitle: 'AI App Builders',
    description:
      'Direct, browser-first product-building alternatives for turning prompts into deployed applications.',
  },
  A: {
    label: 'Tier A',
    subtitle: 'Developer Coding Clients',
    description:
      'Editor, terminal, desktop, and multi-agent products with strong developer search and evaluation intent.',
  },
  B: {
    label: 'Tier B',
    subtitle: 'Specialized Coding Agents',
    description:
      'Adjacent tools for autonomous work, GitHub tasks, migrations, ticket-to-PR workflows, and focused engineering automation.',
  },
};

const tools: AiTool[] = [
  // ── Tier S ──────────────────────────────────────────────────────────────────
  {
    tier: 'S',
    name: 'Lovable',
    company: 'Lovable Labs',
    url: 'https://lovable.dev/',
    pricing: 'Free tier',
    tagline: 'Conversational full-stack web app builder',
    description:
      'Lovable turns natural-language requests into editable full-stack web applications. It combines a visual preview, Build and Plan modes, code editing, Lovable Cloud, integrations, two-way Git sync, publishing, collaboration, and enterprise governance in one browser-led workflow.',
  },
  {
    tier: 'S',
    name: 'Bolt.new',
    company: 'StackBlitz',
    url: 'https://bolt.new/',
    pricing: 'Free tier',
    tagline: 'Browser-based conversational web and mobile app builder',
    description:
      'Bolt.new combines conversational agents with an in-browser development environment, live preview, editable source code, Bolt Cloud, GitHub, Figma and Expo integrations, and one-click hosting. Especially suited to JavaScript web stacks and cross-platform mobile projects built with Expo.',
  },
  {
    tier: 'S',
    name: 'Emergent',
    company: 'Emergent Labs',
    url: 'https://emergent.sh/',
    pricing: 'Free tier',
    tagline: 'Agentic full-stack web and mobile app builder',
    description:
      'Emergent uses conversational agents to create production-oriented React, FastAPI or Node.js, MongoDB, and Expo applications. It spans planning, code generation, preview, automated testing, GitHub version control, managed deployment, custom agents, and web-plus-mobile delivery.',
  },
  {
    tier: 'S',
    name: 'Base44',
    company: 'Wix',
    url: 'https://base44.com/',
    pricing: 'Free tier',
    tagline: 'No-code AI web app builder with managed backend',
    description:
      'Base44 turns natural-language requests into full-stack web applications with database, authentication, permissions, backend functions, storage, integrations, analytics, hosting, and a responsive visual editor. Builder and higher plans add two-way GitHub sync and deeper code access.',
  },
  {
    tier: 'S',
    name: 'Replit',
    company: 'Replit',
    url: 'https://replit.com/',
    pricing: 'Free tier',
    tagline: 'Cloud development workspace with an autonomous app-building agent',
    description:
      'Replit combines Agent with a browser project editor, Canvas, Visual Editor, cloud development environments, databases, authentication, connectors, background tasks, collaboration, security scanning, and publishing. It supports technical and non-technical builders across web, mobile, data, automation, and other artifact types.',
  },
  {
    tier: 'S',
    name: 'v0',
    company: 'Vercel',
    url: 'https://v0.dev/',
    pricing: 'Free tier',
    tagline: 'Browser-based AI app builder and deployment workspace',
    description:
      "v0 is Vercel's browser-based AI agent for turning prompts, mockups, and existing repositories into real applications. It combines chat-led generation, a full browser code editor, per-chat cloud sandboxes, GitHub workflows, team collaboration, a platform API, and direct Vercel deployment.",
  },

  // ── Tier A ──────────────────────────────────────────────────────────────────
  {
    tier: 'A',
    name: 'Codex',
    company: 'OpenAI',
    url: 'https://openai.com/codex',
    pricing: 'Free tier',
    tagline: 'Cross-surface coding agent and multi-agent command center',
    description:
      "Codex is OpenAI's coding agent for writing, reviewing, and shipping code. It spans the ChatGPT desktop app, IDE extension, CLI, web and cloud, with worktrees, multi-agent workflows, skills, scheduled work, and code review.",
  },
  {
    tier: 'A',
    name: 'Cursor',
    company: 'Anysphere',
    url: 'https://cursor.com/',
    pricing: 'Free tier',
    tagline: 'AI-native code editor and agent workspace',
    description:
      'Cursor is an editor-first AI development environment. It combines Cursor Tab, an agent-capable desktop editor, the Agents Window, a CLI, cloud agents, automations, code review, and mobile access in one product family.',
  },
  {
    tier: 'A',
    name: 'Claude Code',
    company: 'Anthropic',
    url: 'https://claude.ai/code',
    pricing: 'Paid entry',
    tagline: 'Cross-surface agentic coding environment',
    description:
      "Claude Code is Anthropic's agentic development environment. It reads codebases, edits files, runs commands, works with Git, and connects external tools. The product spans a full terminal CLI, VS Code and JetBrains integrations, the Claude Desktop Code tab, browser-based cloud sessions, mobile task control, CI integrations, and an Agent SDK.",
  },
  {
    tier: 'A',
    name: 'Windsurf',
    company: 'Cognition',
    url: 'https://windsurf.com/',
    pricing: 'Free tier',
    tagline: 'AI-native desktop editor transitioning to Devin Desktop',
    description:
      "Windsurf was an AI-native desktop editor centered on Cascade, Tab completion, codebase context, terminal tools, MCP, rules, and app deployment. Cognition's current documentation redirects Windsurf editor pages to Devin Desktop and describes Devin Local as the primary local agent.",
  },
  {
    tier: 'A',
    name: 'BLACKBOX AI',
    company: 'Blackbox AI Technologies',
    url: 'https://www.blackbox.ai/',
    pricing: 'Free tier',
    tagline: 'Multi-surface AI coding and agent execution platform',
    description:
      'BLACKBOX AI combines a full AI-native editor, a widely installed VS Code extension, a multi-agent CLI, remote cloud agents, mobile and web surfaces, and agent and model APIs. Agents can work inside an editor, run from a terminal, execute remotely in parallel, or be embedded programmatically.',
  },
  {
    tier: 'A',
    name: 'OJO',
    company: 'OJO',
    url: null,
    pricing: 'Pricing unconfirmed',
    tagline: 'Private-beta Design Agent Team Workspace',
    description:
      'OJO describes itself as a Design Agent Team Workspace that turns an idea into real product design. As of mid-2026, the official site contains only a private-beta landing page and waitlist. Pricing, editor mechanics, agent capabilities, integrations, and enterprise controls are not publicly confirmed.',
    status: 'private-beta',
  },
  {
    tier: 'A',
    name: 'Amazon Q Developer',
    company: 'Amazon Web Services',
    url: 'https://aws.amazon.com/q/developer/',
    pricing: 'Free tier',
    tagline: 'Enterprise coding assistant integrated with AWS development and operations',
    description:
      'Amazon Q Developer combines inline completion, chat, agentic coding, security review, Java and .NET transformation, AWS expertise, repository customization, and enterprise administration. Available across popular IDEs, the AWS console, GitHub preview, GitLab, and operational chat surfaces.',
  },
  {
    tier: 'A',
    name: 'TRAE',
    company: 'TRAE',
    url: 'https://trae.ai/',
    pricing: 'Free tier',
    tagline: 'AI-native desktop IDE plus cross-device autonomous agent workspace',
    description:
      'TRAE combines a full AI-native desktop IDE with TRAE Work, an agent-first workspace across desktop, web, and mobile. Its coding workflows include agent mode, completion and next-edit prediction, skills, rules, memory, MCP, hooks, subagents, Git worktrees, browser preview, visual editing, and one-click application delivery.',
  },
  {
    tier: 'A',
    name: 'Zed',
    company: 'Zed Industries',
    url: 'https://zed.dev/',
    pricing: 'Free tier',
    tagline: 'Native collaborative code editor and multi-agent workspace',
    description:
      'Zed is a native, open-source code editor for macOS, Linux, and Windows that combines fast local editing, real-time multiplayer collaboration, native AI features, external ACP agents, terminal-based agent threads, and parallel worktree workflows. It supports Zed-hosted models, bring-your-own keys, subscriptions, gateways, and local models.',
  },
  {
    tier: 'A',
    name: 'Kiro',
    company: 'Amazon Web Services',
    url: 'https://kiro.dev/',
    pricing: 'Free tier',
    tagline: 'Spec-driven agentic IDE, CLI, and web platform',
    description:
      "Kiro is an AWS agentic development environment with a desktop IDE, terminal CLI, ACP integrations, a browser-based cloud agent, and scheduled automations. Its defining workflow combines structured Specs, persistent Steering, Agent Hooks, custom agents and subagents, MCP, Powers, and shared credit-based access across supported surfaces.",
  },
  {
    tier: 'A',
    name: 'Augment Code',
    company: 'Augment Computing',
    url: 'https://augmentcode.com/',
    pricing: 'Paid entry',
    tagline: 'Enterprise coding-agent and context platform',
    description:
      'Augment Code combines IDE extensions, the Auggie terminal agent, a deep Context Engine, MCP and SDK context services, end-to-end coding agents, subagents, GitHub code review, and the Cosmos automation platform. Business tier costs $100/month for up to 50 seats with pooled usage.',
  },
  {
    tier: 'A',
    name: 'Warp',
    company: 'Warp, Inc.',
    url: 'https://warp.dev/',
    pricing: 'Free tier',
    tagline: 'Native terminal, code editor, and cloud-agent orchestration platform',
    description:
      'Warp has expanded from a modern native terminal into an agentic development environment. Warp Code adds a local editor, codebase context, rules, visual diffs, and code review. Oz adds local and cloud agents, parallel orchestration, schedules, integrations, web management, CLI, API, SDK, and Warp-hosted or self-hosted Docker execution.',
  },
  {
    tier: 'A',
    name: 'Tabnine',
    company: 'Tabnine',
    url: 'https://www.tabnine.com/',
    pricing: 'Paid entry',
    tagline: 'Enterprise AI coding assistant and governed agentic development platform',
    description:
      'Tabnine combines IDE code completion and chat with autonomous agents, a terminal CLI, a cross-repository Context Engine, and optional headless CI/CD agents. Organizations can use SaaS, VPC, on-premises, or fully air-gapped configurations while applying model, cost, permission, and coding-standard controls.',
  },
  {
    tier: 'A',
    name: 'Qodo',
    company: 'Qodo',
    url: 'https://qodo.ai/',
    pricing: 'Paid entry',
    tagline: 'Cross-surface AI code review, standards, and governance platform',
    description:
      'Qodo is an AI code review and governance platform for validating human- and AI-generated changes. Its product focuses on real-time IDE review, multi-agent pull-request analysis, cross-repository context, review standards, compliance, and remediation. Qodo deprecated autocomplete and chat-based code generation in April 2026.',
  },
  {
    tier: 'A',
    name: 'Junie',
    company: 'JetBrains',
    url: 'https://www.jetbrains.com/junie/',
    pricing: 'Free tier',
    tagline: 'LLM-agnostic local coding agent across terminal, IDE, and CI/CD',
    description:
      "Junie is JetBrains' LLM-agnostic coding agent for implementing features, fixing bugs, debugging, reviewing code, and running development workflows. It operates in a standalone CLI, JetBrains IDEs, ACP-compatible clients, GitHub Actions, and GitLab CI/CD, with plan mode, approval controls, MCP, persistent guidelines, reusable skills, and remote browser supervision.",
  },

  // ── Tier B ──────────────────────────────────────────────────────────────────
  {
    tier: 'B',
    name: 'Pythagora',
    company: 'Pythagora / GPT Pilot',
    url: 'https://pythagora.ai/',
    pricing: 'Legacy — pricing unconfirmed',
    tagline: 'Legacy VS Code AI app builder and open-source multi-agent coding core',
    description:
      'Pythagora was a VS Code-centered AI development platform built on GPT Pilot, using cooperating agents to plan, implement, review, debug, and document full-stack applications. The GPT Pilot repository is no longer maintained and pythagora.ai redirects to a different product. Evaluate as a legacy tool rather than a currently sold coding client.',
    status: 'legacy',
  },
  {
    tier: 'B',
    name: 'Devin',
    company: 'Cognition',
    url: 'https://devin.ai/',
    pricing: 'Free tier',
    tagline: 'Cloud autonomous software engineer and delegated task workspace',
    description:
      "Devin is Cognition's autonomous AI software engineer. It can inspect a codebase, plan work, edit files, run commands and tests, browse the web, and open pull requests from a cloud development environment. The product spans a web and PWA workspace, Devin for Terminal, API and team integrations, with separate Devin Review and DeepWiki products.",
  },
  {
    tier: 'B',
    name: 'Jules',
    company: 'Google',
    url: 'https://jules.google/',
    pricing: 'Free tier',
    tagline: 'GitHub-first asynchronous coding agent in Google-managed cloud VMs',
    description:
      "Jules is Google's autonomous coding agent for GitHub repositories. It clones a repository into a short-lived cloud virtual machine, plans the work, edits code, runs tests, and returns a branch and pull request for review. It supports parallel tasks, issue-label triggers, a CLI, an alpha API, and integrations for development workflows.",
  },
  {
    tier: 'B',
    name: 'GitHub Copilot',
    company: 'GitHub',
    url: 'https://github.com/features/copilot',
    pricing: 'Free tier',
    tagline: 'GitHub-native AI developer platform with editor, terminal, desktop, and cloud agents',
    description:
      'GitHub Copilot combines inline code completion, chat, agent mode, code review, a cloud coding agent, Copilot CLI, GitHub.com and mobile experiences, and a desktop app in technical preview. It integrates deeply with GitHub delivery workflows while adding custom agents, skills, MCP, plugins, and an SDK.',
  },
  {
    tier: 'B',
    name: 'Codegen',
    company: 'ClickUp',
    url: 'https://clickup.com/features/codegen',
    pricing: 'Pricing unconfirmed',
    tagline: 'ClickUp-native task-to-pull-request coding agent',
    description:
      'ClickUp acquired Codegen, Inc. in December 2025 and deprecated the independent Codegen service on January 16, 2026. The current ClickUp Codegen product works inside ClickUp: it reads task context, writes code, opens a pull request, and posts status updates without requiring a separate download.',
    status: 'deprecated',
  },
  {
    tier: 'B',
    name: 'Aider',
    company: 'Aider AI',
    url: 'https://aider.chat/',
    pricing: 'Free (open source)',
    tagline: 'Terminal-native AI pair programmer for local Git repositories',
    description:
      'Aider is an open-source command-line coding agent built around local files and Git. It connects to hosted or local models, builds a compact repository map, edits multiple files, commits changes, runs lint and test commands, and supports code, ask, and two-model architect workflows.',
  },
  {
    tier: 'B',
    name: 'Cline',
    company: 'Cline Bot',
    url: 'https://cline.bot/',
    pricing: 'Free (open source)',
    tagline: 'Open-source local coding agent across IDE, CLI, and SDK',
    description:
      'Cline is an open-source agent that can inspect code, edit files, run commands, browse the web, and use MCP tools with approval controls. Its current product family includes VS Code, CLI, SDK, Kanban multi-agent workflows, scheduled agents, messaging connectors, and an Enterprise edition.',
  },
  {
    tier: 'B',
    name: 'Amp',
    company: 'Amp Frontier Corporation',
    url: 'https://ampcode.com/',
    pricing: 'Free tier',
    tagline: 'Terminal-first, multi-model coding agent',
    description:
      'Amp is an opinionated coding agent for terminal and editor workflows. It combines interactive threads, multiple reasoning modes, tools and shell execution, custom subagents, skills, plugins, MCP, remote control, remote machines called Orbs, runners, code review, a TypeScript SDK, and usage-based pricing at provider cost for non-enterprise workspaces.',
  },
  {
    tier: 'B',
    name: 'Antigravity CLI',
    company: 'Google',
    url: 'https://deepmind.google/technologies/gemini/',
    pricing: 'Free tier',
    tagline: 'Antigravity multi-agent terminal and desktop platform (successor to Gemini CLI)',
    description:
      'Google transitioned consumer Gemini CLI users to Antigravity CLI in 2026. Antigravity CLI is now the consumer terminal experience, sharing a server-side harness with Antigravity 2.0 and adding asynchronous subagents, skills, hooks, plugins, MCP, permissions, and background work. Gemini CLI itself remains supported for organizations using Gemini Code Assist Standard or Enterprise.',
  },
];

const PRICING_STYLE: Record<string, string> = {
  'Free tier': 'bg-limedim text-lime',
  'Free (open source)': 'bg-limedim text-lime',
  'Paid entry': 'bg-accent-amber/10 text-accent-amber',
  'Pricing unconfirmed': 'bg-charcoal-card2 text-faint border border-charcoal-border',
  'Legacy — pricing unconfirmed': 'bg-charcoal-card2 text-faint border border-charcoal-border',
};

const STATUS_BADGE: Record<string, string> = {
  legacy: 'Legacy',
  'private-beta': 'Private beta',
  deprecated: 'Deprecated (standalone)',
};

function ToolCard({ tool, index }: { tool: AiTool; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const short = tool.description.slice(0, 140);
  const hasMore = tool.description.length > 140;

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-mono text-faint shrink-0">{String(index + 1).padStart(2, '0')}</span>
          <h3 className="text-lg font-bold text-gray-900 truncate">{tool.name}</h3>
          {tool.status && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium shrink-0">
              {STATUS_BADGE[tool.status]}
            </span>
          )}
        </div>
        <span
          className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap shrink-0 ${
            PRICING_STYLE[tool.pricing] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          {tool.pricing}
        </span>
      </div>

      <p className="text-xs text-faint mb-2">{tool.company}</p>
      <p className="text-sm font-semibold text-blue-700 mb-2">{tool.tagline}</p>

      <p className="text-sm text-gray-700 leading-relaxed">
        {expanded || !hasMore ? tool.description : `${short}…`}
      </p>

      {hasMore && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs text-blue-600 hover:underline flex items-center gap-0.5"
        >
          {expanded ? (
            <>Less <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>More <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}

      {tool.url && (
        <div className="mt-4">
          <a
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center text-sm py-1.5 px-4"
          >
            Visit site <ExternalLink className="h-3 w-3 ml-1.5" />
          </a>
        </div>
      )}
    </div>
  );
}

const AiCodingTools: React.FC = () => {
  const title = 'Best AI Coding Tools (2026) — 30 Source-Reviewed Clients Compared';
  const description =
    'A complete, source-reviewed directory of 30 AI coding tools in 2026. Compare AI app builders (Lovable, Bolt.new, v0), developer coding clients (Cursor, Claude Code, GitHub Copilot), and specialized agents (Devin, Aider, Cline) — with pricing and key features.';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${PAGE_URL}#webpage`,
        url: PAGE_URL,
        name: title,
        description,
        isPartOf: { '@id': `${SITE}/#website` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'ItemList',
        '@id': `${PAGE_URL}#list`,
        name: 'AI Coding Tools (2026)',
        description: 'Source-reviewed directory of AI coding tools, organized by tier.',
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        numberOfItems: tools.length,
        itemListElement: tools.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: t.name,
          ...(t.url ? { url: t.url } : {}),
          description: t.tagline,
        })),
      },
    ],
  };

  const tierGroups = (['S', 'A', 'B'] as Tier[]).map((tier) => ({
    tier,
    tools: tools.filter((t) => t.tier === tier),
  }));

  const tierBadgeStyle: Record<Tier, string> = {
    S: 'bg-limedim text-lime border border-lime/30',
    A: 'bg-accent-teal/10 text-accent-teal border border-accent-teal/30',
    B: 'bg-charcoal-card2 text-muted border border-charcoal-border',
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-charcoal-bg min-h-full">
        <div className="max-w-5xl mx-auto px-4 py-12">

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">AI Coding Tools</span>
          </nav>

          {/* Header */}
          <header className="mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              AI Coding Tools (2026)
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              A complete directory of {tools.length} source-reviewed AI coding tools, organized by
              tier — from browser-first app builders to specialized autonomous agents.
            </p>
            <p className="text-sm text-gray-500">
              Tool data sourced from{' '}
              <a
                href="https://graphify.net/ai-coding-tools/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Graphify's source-reviewed client directory
              </a>
              . Descriptions reflect publicly confirmed capabilities as of mid-2026.
            </p>
          </header>

          {/* Tier count pills */}
          <div className="flex flex-wrap gap-3 mb-10">
            {tierGroups.map(({ tier, tools: ts }) => (
              <a
                key={tier}
                href={`#tier-${tier}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${tierBadgeStyle[tier]}`}
              >
                <span>{TIER_INFO[tier].label}</span>
                <span className="opacity-70">·</span>
                <span>{TIER_INFO[tier].subtitle}</span>
                <span className="font-mono opacity-60">({ts.length})</span>
              </a>
            ))}
          </div>

          {/* Tier sections */}
          {tierGroups.map(({ tier, tools: ts }, gi) => {
            const info = TIER_INFO[tier];
            const startIndex = tierGroups.slice(0, gi).reduce((acc, g) => acc + g.tools.length, 0);
            return (
              <section key={tier} id={`tier-${tier}`} className="mb-14">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`text-xs font-bold px-3 py-1 rounded-full ${tierBadgeStyle[tier]}`}
                  >
                    {info.label}
                  </span>
                  <h2 className="text-2xl font-bold text-gray-900">{info.subtitle}</h2>
                </div>
                <p className="text-gray-600 mb-6 text-sm">{info.description}</p>

                <div className="grid gap-4 sm:grid-cols-2">
                  {ts.map((tool, i) => (
                    <ToolCard key={tool.name} tool={tool} index={startIndex + i} />
                  ))}
                </div>
              </section>
            );
          })}

          {/* Contextual note about security */}
          <section className="card mb-10">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              Scanning AI-generated code for security risks
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              AI coding tools dramatically speed up development — but they can also introduce
              security vulnerabilities specific to AI-generated ("vibe-coded") code: prompt injection
              surfaces, insecure LLM API usage, hardcoded secrets, and dependency CVEs. After using
              any of the tools above, scanning your repository with GitHubMate takes 30 seconds and
              covers OWASP LLM Top 10 (2025), OWASP Top 10:2021, secret scanning, live CVE lookup,
              and IaC misconfigurations — free, no signup.
            </p>
          </section>

          {/* CTA */}
          <section className="rounded-3xl border border-charcoal-border bg-gradient-to-br from-lime/15 via-charcoal-card to-accent-teal/10 p-8 text-center text-gray-900">
            <h2 className="text-2xl font-bold mb-3">Scan your AI-generated code for free</h2>
            <p className="text-muted max-w-2xl mx-auto mb-6">
              GitHubMate scans any GitHub repository for OWASP LLM Top 10 risks, secrets, CVEs, and
              IaC misconfigurations in about 30 seconds — free, no signup required.
            </p>
            <Link to="/" className="btn-primary inline-flex items-center py-3 px-6">
              <Shield className="h-5 w-5 mr-2" /> Scan a repository free
            </Link>
          </section>

          {/* Cross-links */}
          <nav className="mt-10 text-sm text-center" aria-label="Related pages">
            <span className="text-gray-500">Also see: </span>
            <Link to="/best-ai-code-analysis-tools" className="text-blue-600 hover:underline mx-2">
              Best AI code analysis tools
            </Link>
            <span className="text-gray-300">·</span>
            <Link to="/compare" className="text-blue-600 hover:underline mx-2">
              GitHubMate comparisons
            </Link>
          </nav>

        </div>
      </div>
    </>
  );
};

export default AiCodingTools;
